import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, getCurrentMonth } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Upload, ChevronLeft, ChevronRight, Search, Check, X, ArrowDown, ArrowUp, Landmark, FileSpreadsheet, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const CATEGORIES = [
  'Fournisseur alimentaire', 'Energie / Eau', 'Loyer', 'Charges sociales',
  'Impots / Taxes', 'Assurance', 'Salaires', 'Paiement carte', 'Recette',
  'Prelevement', 'Cheque', 'Autre',
];

const CAT_COLORS = {
  'Fournisseur alimentaire': 'text-orange-400',
  'Energie / Eau': 'text-blue-400',
  'Loyer': 'text-purple-400',
  'Charges sociales': 'text-red-400',
  'Impots / Taxes': 'text-red-300',
  'Assurance': 'text-cyan-400',
  'Salaires': 'text-pink-400',
  'Paiement carte': 'text-yellow-400',
  'Recette': 'text-green-400',
  'Prelevement': 'text-gray-400',
  'Cheque': 'text-indigo-400',
  'Autre': 'text-gray-500',
};

const BanquePage = () => {
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [comptes, setComptes] = useState([]);
  const [annuel, setAnnuel] = useState(null);
  const [editingCat, setEditingCat] = useState(null);

  const m = parseInt(currentMonth.split('-')[1]);
  const moisLabel = MONTHS_FR[m - 1] || '';

  const fetchData = useCallback(async () => {
    try {
      const [opsRes, comptesRes, annuelRes] = await Promise.all([
        api.get(`/banque/operations/${currentMonth}`),
        api.get('/banque/comptes'),
        api.get(`/banque/annuel/${currentMonth.slice(0, 4)}`),
      ]);
      setData(opsRes.data);
      setComptes(comptesRes.data);
      setAnnuel(annuelRes.data);
    } catch { /* empty */ }
    setLoading(false);
  }, [currentMonth]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const prevMonth = () => {
    const d = new Date(currentMonth + '-01');
    d.setMonth(d.getMonth() - 1);
    setCurrentMonth(d.toISOString().slice(0, 7));
  };
  const nextMonth = () => {
    const d = new Date(currentMonth + '-01');
    d.setMonth(d.getMonth() + 1);
    setCurrentMonth(d.toISOString().slice(0, 7));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('compte', 'principal');
    formData.append('banque', file.name.toLowerCase().includes('agricole') ? 'Credit Agricole' : 'Banque Populaire');
    try {
      const res = await api.post('/banque/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.imported} operations importees (${res.data.skipped} ignorees)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    }
    setUploading(false);
    e.target.value = '';
  };

  const updateCategorie = async (opId, newCat) => {
    try {
      await api.put(`/banque/operation/${opId}`, { categorie: newCat });
      setData(prev => ({
        ...prev,
        operations: prev.operations.map(o => o.id === opId ? { ...o, categorie: newCat } : o),
      }));
      setEditingCat(null);
      toast.success('Categorie modifiee');
    } catch { toast.error('Erreur'); }
  };

  const togglePointee = async (opId, current) => {
    try {
      await api.put(`/banque/operation/${opId}`, { pointee: !current });
      setData(prev => ({
        ...prev,
        operations: prev.operations.map(o => o.id === opId ? { ...o, pointee: !current } : o),
      }));
    } catch { toast.error('Erreur'); }
  };

  // Filter operations
  const filtered = (data?.operations || []).filter(op => {
    if (search && !op.libelle.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCat && op.categorie !== filterCat) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4 max-w-6xl mx-auto" data-testid="banque-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Landmark size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Banque</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer" data-testid="import-csv-btn">
            <input type="file" accept=".csv" onChange={handleUpload} className="hidden" />
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              uploading ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]' : 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 cursor-pointer'
            }`}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Import...' : 'Importer CSV'}
            </div>
          </label>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-center gap-4">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="prev-month">
          <ChevronLeft size={18} />
        </button>
        <h2 className="text-base font-title text-[rgb(var(--lm-text))]" data-testid="month-label">{moisLabel} {currentMonth.slice(0, 4)}</h2>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="next-month">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Summary cards */}
      {data && data.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Recettes</p>
            <p className="font-mono text-lg text-green-400" data-testid="total-credit">{formatCurrency(data.total_credit)}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Depenses</p>
            <p className="font-mono text-lg text-red-400" data-testid="total-debit">{formatCurrency(Math.abs(data.total_debit))}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Solde</p>
            <p className={`font-mono text-lg ${data.solde >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="solde">{formatCurrency(data.solde)}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Operations</p>
            <p className="font-mono text-lg text-[rgb(var(--lm-text))]" data-testid="total-ops">{data.total}</p>
          </div>
        </div>
      )}

      {/* Categories breakdown */}
      {data?.par_categorie?.length > 0 && (
        <div className="lm-card p-4">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Par categorie</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">
            {data.par_categorie.map(c => (
              <button key={c.categorie} onClick={() => setFilterCat(filterCat === c.categorie ? '' : c.categorie)}
                className={`flex items-center justify-between py-1 px-2 rounded-lg text-xs transition-all ${
                  filterCat === c.categorie ? 'bg-[rgb(var(--lm-surface-elevated))]' : 'hover:bg-[rgb(var(--lm-surface-elevated)/0.5)]'
                }`} data-testid={`cat-filter-${c.categorie}`}>
                <span className={`truncate ${CAT_COLORS[c.categorie] || 'text-gray-400'}`}>{c.categorie}</span>
                <span className={`font-mono ml-2 shrink-0 ${c.total < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {formatCurrency(Math.abs(c.total))}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search & filter bar */}
      {data && data.total > 0 && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] pl-9 text-sm"
              placeholder="Rechercher..." data-testid="search-input" />
          </div>
          {filterCat && (
            <Button size="sm" variant="ghost" onClick={() => setFilterCat('')}
              className="text-xs text-[rgb(var(--lm-text-muted))]" data-testid="clear-filter">
              <X size={12} className="mr-1" /> {filterCat}
            </Button>
          )}
        </div>
      )}

      {/* Operations list */}
      {(!data || data.total === 0) ? (
        <div className="lm-card p-8 text-center">
          <FileSpreadsheet size={32} className="mx-auto mb-3 text-[rgb(var(--lm-text-muted)/0.3)]" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))] mb-2">Aucune operation pour {moisLabel}</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted)/0.6)]">
            Importe ton releve CSV depuis Credit Agricole ou Banque Populaire
          </p>
          <label className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] text-xs font-medium cursor-pointer hover:opacity-90"
            data-testid="import-empty-btn">
            <Upload size={14} /> Importer CSV
            <input type="file" accept=".csv" onChange={handleUpload} className="hidden" />
          </label>
        </div>
      ) : (
        <div className="space-y-0.5" data-testid="operations-list">
          {filtered.map(op => (
            <div key={op.id} className={`lm-card px-4 py-2.5 flex items-center gap-3 group transition-colors ${op.pointee ? 'opacity-50' : ''}`}
              data-testid={`op-${op.id}`}>
              {/* Pointee checkbox */}
              <button onClick={() => togglePointee(op.id, op.pointee)}
                className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${
                  op.pointee ? 'bg-green-500/20 border-green-500/30 text-green-400' : 'border-[rgb(var(--lm-border))] hover:border-[rgb(var(--lm-gold))]'
                }`} data-testid={`pointee-${op.id}`}>
                {op.pointee && <Check size={12} />}
              </button>

              {/* Date */}
              <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))] w-16 shrink-0">{op.date.slice(5)}</span>

              {/* Libelle + categorie */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[rgb(var(--lm-text))] truncate">{op.libelle}</p>
                {editingCat === op.id ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {CATEGORIES.map(cat => (
                      <button key={cat} onClick={() => updateCategorie(op.id, cat)}
                        className={`text-[9px] px-1.5 py-0.5 rounded border transition-all ${
                          op.categorie === cat ? 'bg-[rgb(var(--lm-gold)/0.2)] border-[rgb(var(--lm-gold)/0.3)] text-[rgb(var(--lm-gold))]' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'
                        }`}>
                        {cat}
                      </button>
                    ))}
                    <button onClick={() => setEditingCat(null)} className="text-[9px] text-[rgb(var(--lm-text-muted))]">
                      <X size={10} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEditingCat(op.id)}
                    className={`text-[9px] ${CAT_COLORS[op.categorie] || 'text-gray-500'} hover:underline`}>
                    {op.categorie}
                  </button>
                )}
              </div>

              {/* Amount */}
              <span className={`font-mono text-sm font-medium shrink-0 ${op.montant < 0 ? 'text-red-400' : 'text-green-400'}`}>
                {op.montant > 0 ? '+' : ''}{formatCurrency(op.montant)}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-xs text-[rgb(var(--lm-text-muted))] py-4">Aucun resultat</p>
          )}
        </div>
      )}

      {/* Annual chart */}
      {annuel && (
        <div className="lm-card p-4">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">
            Annee {annuel.annee}
          </p>
          <div className="flex items-end gap-1 h-24">
            {annuel.mois.map(m => {
              const maxVal = Math.max(...annuel.mois.map(x => Math.max(Math.abs(x.debit), x.credit)), 1);
              const creditH = m.credit > 0 ? Math.max(4, (m.credit / maxVal) * 100) : 0;
              const debitH = m.debit < 0 ? Math.max(4, (Math.abs(m.debit) / maxVal) * 100) : 0;
              const isActive = m.mois === currentMonth;
              return (
                <button key={m.mois} onClick={() => setCurrentMonth(m.mois)}
                  className="flex-1 flex flex-col items-center justify-end gap-0.5 group cursor-pointer"
                  data-testid={`annual-month-${m.mois}`}>
                  <div className="w-full flex gap-px justify-center" style={{ height: `${Math.max(creditH, debitH)}%`, minHeight: (m.credit > 0 || m.debit < 0) ? '4px' : '0' }}>
                    {m.credit > 0 && <div className="flex-1 rounded-t-sm" style={{ height: `${creditH}%`, background: isActive ? 'rgb(74,222,128)' : 'rgba(74,222,128,0.25)' }} />}
                    {m.debit < 0 && <div className="flex-1 rounded-t-sm" style={{ height: `${debitH}%`, background: isActive ? 'rgb(248,113,113)' : 'rgba(248,113,113,0.25)' }} />}
                  </div>
                  <span className={`text-[8px] uppercase ${isActive ? 'text-[rgb(var(--lm-gold))] font-medium' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                    {m.mois_label.slice(0, 1)}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex justify-center gap-4 mt-2 text-[9px] text-[rgb(var(--lm-text-muted))]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-400" /> Recettes</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400" /> Depenses</span>
          </div>
        </div>
      )}

      {/* Connected accounts */}
      {comptes.length > 0 && (
        <div className="lm-card p-4">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Comptes connectes</p>
          {comptes.map((c, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <Landmark size={12} className="text-[rgb(var(--lm-gold))]" />
                <span className="text-[rgb(var(--lm-text))]">{c.banque} — {c.compte}</span>
              </div>
              <span className="text-[rgb(var(--lm-text-muted))] font-mono">{c.nb_operations} ops · {c.periode}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BanquePage;
